// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { visitApi } from 'src/api/visit';
import { VisitSummaryPDF } from 'src/components/visit-summary-pdf';
import { translateKey } from 'src/locales/translate';
import {
  GetVisitById,
  GetVisitByIdCancelledByEnum,
  GetVisitByIdStatusEnum,
  VisitSummaryDataDto,
} from 'woofwoof-api';
import OrganizationVisitModalDetailsMembers from './OrganizationVisitModalDetailsMembers';
import OrganizationVisitModalDetailsTab from './OrganizationVisitModalDetailsTab';
import OrganizationVisitModalEditTab from './OrganizationVisitModalEditTab';
import { useVisitModalAction } from './VisitModalActionContext';
import { VISIT_MODAL_PAPER_MEDIA } from './visitModalLayout';

type OrganizationVisitModalDetailsTabsProps = {
  visit?: GetVisitById;
  isConversationLoading?: boolean;
  onClose: () => void;
  isOpen: boolean;
  onReschedule?: ({
    professionalId,
    date,
    visitId,
  }: {
    professionalId: string;
    date: string;
    visitId: string;
  }) => void;
  isRescheduleLoading?: boolean;
  onCancel?: () => void;
  isCancelLoading?: boolean;
  editOnly?: boolean;
};

export default function OrganizationVisitModalDetailsTabs({
  visit,
  isConversationLoading,
  onClose,
  isOpen,
  onReschedule,
  isRescheduleLoading,
  onCancel,
  isCancelLoading,
  editOnly,
}: OrganizationVisitModalDetailsTabsProps) {
  const theme = useTheme();
  const isDown500Px = useMediaQuery((theme: any) =>
    theme.breakpoints.down(500),
  );
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState(editOnly ? 2 : 1);
  const {
    action: contextAction,
    addAction,
    setSummaryAvailable,
    registerSummaryHandler,
  } = useVisitModalAction();
  // `generatingMode` drives which confirm-dialog button shows its spinner:
  // 'send' = Generuj i wyślij (attach + notify owner), 'local' = Generuj only.
  const [generatingMode, setGeneratingMode] = useState<'send' | 'local' | null>(
    null,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [summaryData, setSummaryData] = useState<VisitSummaryDataDto | null>(
    null,
  );
  const [loadingSummaryData, setLoadingSummaryData] = useState(false);
  const queryClient = useQueryClient();

  const isGuestVisit = !visit?.profile?.id;

  // Helper function to fetch summary data
  const fetchSummaryData = useCallback(async () => {
    if (!visit?.id || visit.status !== GetVisitByIdStatusEnum.Completed) return;

    setLoadingSummaryData(true);
    try {
      const response = await visitApi.visitControllerGetVisitSummaryData({
        visitId: visit.id,
      });
      setSummaryData(response.data);
    } catch (error) {
      console.error('Failed to fetch summary data:', error);
    } finally {
      setLoadingSummaryData(false);
    }
  }, [visit?.id, visit?.status]);

  // Fetch summary data when visit is completed
  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // Publish the "Podsumowanie wizyty" menu entry (rendered by VisitAddMenu).
  // Clicking it always opens the generate-confirm dialog — the professional
  // decides there whether to just generate or generate + send. No
  // already-generated / changed-since-last-time logic.
  useEffect(() => {
    const summaryReady =
      visit?.status === GetVisitByIdStatusEnum.Completed &&
      !loadingSummaryData &&
      !!summaryData;

    setSummaryAvailable(summaryReady);
    registerSummaryHandler(summaryReady ? () => setConfirmOpen(true) : null);

    return () => {
      setSummaryAvailable(false);
      registerSummaryHandler(null);
    };
  }, [
    visit?.status,
    loadingSummaryData,
    summaryData,
    setSummaryAvailable,
    registerSummaryHandler,
  ]);

  if (!visit) {
    return null;
  }

  const isVisitCompleted = visit.status === GetVisitByIdStatusEnum.Completed;
  // `isVisitAborted` lets the cancellation-reason banner switch to the
  // abort-specific copy without changing the rest of the cancelled-state UX.
  const isVisitAborted = visit.status === GetVisitByIdStatusEnum.Aborted;
  const isVisitCancelled =
    visit.status === GetVisitByIdStatusEnum.Cancelled || isVisitAborted;
  const isVisitPending = visit.status === GetVisitByIdStatusEnum.Pending;

  const handleGenerateSummary = async (attach: boolean) => {
    if (!visit?.id || !summaryData) return;

    setGeneratingMode(attach ? 'send' : 'local');
    try {
      // Step 1: Generate PDF using react-pdf
      const pdfDocument = <VisitSummaryPDF data={summaryData} />;
      const blob = await pdf(pdfDocument).toBlob();

      // Step 2: Download PDF
      const visitDate = format(
        new Date(summaryData.visitDate),
        'dd.MM.yyyy HH.mm',
        { locale: pl },
      );
      const filename = `Podsumowanie wizyty z dnia ${visitDate}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Step 3: When sending, upload to backend (which also marks as
      // generated and notifies the owner).
      if (attach) {
        // Convert blob to base64
        const base64data = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });

        const pdfBase64 = base64data.split(',')[1]; // Remove data:application/pdf;base64, prefix

        try {
          await visitApi.visitControllerAttachVisitSummary({
            visitId: visit.id,
            visitControllerAttachVisitSummaryRequest: { pdfBase64 },
          });

          enqueueSnackbar(
            isGuestVisit
              ? translateKey(
                  'hardcoded.podsumowanie_wygenerowane_i_dodane_do_dokumentow_wizyty',
                )
              : 'Podsumowanie wygenerowane i dodane do profilu pacjenta',
            { variant: 'success' },
          );

          // Refresh the Dokumenty tab so the newly-attached PDF shows up — the
          // tab reads the visit-scoped documents list, so invalidate that key
          // (the prior ['visitById'] invalidation was a no-op: the modal's
          // visit comes from a list prop, nothing queries that key here). Also
          // refresh the activity list so the generated DOCUMENT/ADDED row lights
          // up.
          queryClient.invalidateQueries(['visit-documents-modal', visit.id]);
          queryClient.invalidateQueries(['visit-activity-modal', visit.id]);

          // Refetch summary data to update UI state (non-critical)
          try {
            await fetchSummaryData();
          } catch (fetchError) {
            // Don't show error toast - upload succeeded
          }
        } catch (error: any) {
          enqueueSnackbar(
            error?.response?.data?.message ||
              translateKey('hardcoded.blad_podczas_dodawania_do_profilu'),
            { variant: 'error' },
          );
        }
      } else {
        enqueueSnackbar(
          translateKey('hardcoded.podsumowanie_wygenerowane_i_pobrane'),
          { variant: 'success' },
        );
      }
    } catch (error: any) {
      console.log(error);
      enqueueSnackbar(
        error?.response?.data?.message ||
          translateKey('hardcoded.blad_podczas_generowania_podsumowania'),
        { variant: 'error' },
      );
    } finally {
      setGeneratingMode(null);
      setConfirmOpen(false);
    }
  };

  const TABS = [
    {
      value: 1,
      label: translateKey('search.ui.detailsTab'),
      disabled: false,
    },
    {
      value: 2,
      label: translateKey('visit.tabManage'),
      disabled: false,
    },
  ];

  return (
    <DialogContent
      sx={{
        p: 0,
        minHeight: 0,
        // Paper mode (viewport wide + tall enough): a fixed header, a single
        // scrolling body (the active tab) and a fixed footer. Otherwise the
        // whole dialog scrolls with the page (see visitModalLayout / the
        // Dialog's `scroll` prop).
        [VISIT_MODAL_PAPER_MEDIA]: {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* ---------- Fixed header: pre-visit tabs + patient / owner ---------- */}
      <Box sx={{ flexShrink: 0, px: 3, pt: 3, pb: 2 }}>
        {!isVisitCompleted &&
          !isVisitCancelled &&
          !isVisitPending &&
          !editOnly && (
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                borderWidth: '1px',
                borderColor: theme.palette.divider,
                borderBottomStyle: 'solid',
                gap: 2,
                mx: 0,
                mb: 3,
                width: '100%',
              }}
            >
              <Tabs
                value={value}
                onChange={(_, newValue) => setValue(newValue)}
                variant="fullWidth"
                sx={{ width: '100%' }}
              >
                {TABS.map((tab) => (
                  <Tab
                    key={tab.value}
                    data-testid={`visit-tab-${tab.value}`}
                    label={tab.label}
                    value={tab.value}
                    disabled={tab.disabled}
                    sx={{ fontSize: 16 }}
                  />
                ))}
              </Tabs>
            </Stack>
          )}
        <OrganizationVisitModalDetailsMembers visit={visit} />
      </Box>

      {/* ---------- Scrollable body: the active tab content ---------- */}
      <Box
        sx={{
          px: 3,
          pt: value === 1 ? 0 : 3,
          pb: value === 1 ? 0 : 3,
          // Paper mode: the single scroll region, a flex column so the active
          // tab fills the body height (short tabs centre their content).
          [VISIT_MODAL_PAPER_MEDIA]: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {value === 1 && (
          <OrganizationVisitModalDetailsTab
            visit={visit}
            onVisitDataChange={fetchSummaryData}
          />
        )}
        {value === 2 && (
          <OrganizationVisitModalEditTab
            visit={visit}
            onClose={onClose}
            isOpen={value === 2 && isOpen}
            onReschedule={onReschedule}
            isRescheduleLoading={isRescheduleLoading}
            onCancel={onCancel}
            isCancelLoading={isCancelLoading}
          />
        )}
      </Box>

      {/* ---------- Fixed footer: summary actions + close ---------- */}
      {value === 1 && (
        <Box
          sx={{
            flexShrink: 0,
            px: 3,
            py: 2,
          }}
        >
          {isVisitCancelled &&
            (visit.cancellationReason || visit.cancelledBy) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {isVisitAborted ? (
                  <>
                    {translateKey('visit.abortedByProfessional')}
                    {visit.cancellationReason && '. '}
                  </>
                ) : (
                  visit.cancelledBy && (
                    <>
                      {translateKey('visit.cancelledByLabel')}{' '}
                      {visit.cancelledBy === GetVisitByIdCancelledByEnum.Owner
                        ? translateKey('visit.cancelledByOwner')
                        : translateKey('visit.cancelledByClinic')}
                      {visit.cancellationReason && '. '}
                    </>
                  )
                )}
                {visit.cancellationReason}
              </Alert>
            )}
          <Stack
            sx={{
              flexDirection: isDown500Px ? 'column' : 'row',
              gap: 1.5,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              fullWidth={isDown500Px}
              size="medium"
            >
              {translateKey('common.close')}
            </Button>
            <Stack
              sx={{
                flexDirection: isDown500Px ? 'column' : 'row',
                gap: 1.5,
                alignItems: 'center',
                width: isDown500Px ? '100%' : 'auto',
              }}
            >
              {contextAction}
              {addAction}
            </Stack>
          </Stack>
        </Box>
      )}

      {/* ---------- Confirm dialog: generate (and optionally send) summary ---------- */}
      <Dialog
        open={confirmOpen}
        onClose={() => generatingMode === null && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {translateKey('hardcoded.czy_dodac_podsumowanie_wizyty')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {translateKey(
              'hardcoded.wlasciciel_zostanie_poinformowany_o_dodaniu_dokumentu',
            )}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            flexDirection: isDown500Px ? 'column-reverse' : 'row',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setConfirmOpen(false)}
            disabled={generatingMode !== null}
            fullWidth={isDown500Px}
          >
            {translateKey('common.cancel')}
          </Button>
          <Stack
            sx={{
              flexDirection: isDown500Px ? 'column' : 'row',
              gap: 1.5,
              width: isDown500Px ? '100%' : 'auto',
            }}
          >
            <LoadingButton
              variant="contained"
              loading={generatingMode === 'send'}
              disabled={generatingMode === 'local'}
              onClick={() => handleGenerateSummary(true)}
              fullWidth={isDown500Px}
              sx={{
                background: theme.palette.common.black,
                color: theme.palette.common.white,
                textTransform: 'none',
              }}
            >
              {translateKey('hardcoded.generuj_i_wyslij')}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              loading={generatingMode === 'local'}
              disabled={generatingMode === 'send'}
              onClick={() => handleGenerateSummary(false)}
              fullWidth={isDown500Px}
            >
              {translateKey('hardcoded.generuj')}
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Dialog>
    </DialogContent>
  );
}
